import { BaseViewStore, DropdownCellData, ReservedTooltipCellData } from 'core/stores';
import { action, observable, toJS, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { ProductStore, MultiPackageProductStore } from 'modules/product/stores';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { ApplicationStore } from 'platform/application/stores';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, conversionRates, sourceOfChange, channelType } from 'common/constants';
import { ModalParams, ConfirmModalParams } from 'core/models';
import { DescriptionForm } from 'modules/product/forms';
import {
	isSome,
	notificationError,
	isSeller,
	isSuperAdminOrganization,
	isWarehouse,
	isValueNullOrNegative,
	checkForColumnWidth,
	formatDate,
	isCurrentDateGreater,
} from 'core/utils';
import _ from 'lodash';

class ProductDetailViewStore extends BaseViewStore {
	@observable product;
	@observable warehouseProductList;
	@observable showMore = false;
	@observable updateTable = true;
	@observable organizationsCount = 0;
	@observable productInventory = [];
	@observable selectedProductPart = null;
	@observable productPartToDeleteIndex = null;
	@observable scheduledRestockDate = null;
	@observable hasAmazonVirtualSku = false;
	@observable sellerId = null;
	allowEdit = true;
	confirmDisabled = true;
	cancelDisabled = true;
	showCustomActions = false;
	noDataItem = true;
	isInventoryAdd = false;
	textLimit = 80;
	editField = 'inEdit';
	dimensionUnits = [];
	weightUnits = [];
	warehouseActivities = [];
	originalData = [];
	data = [];
	convertedLength;
	convertedHeight;
	convertedWidth;
	convertedWeight;
	dimensionUnit;
	weightUnit;
	localStorageWidthKey = 'product-detail-column-width';
	selectedInventoryForInventoryLog = null;

	descriptionForm = new DescriptionForm({
		onSuccess: async () => {
			await this.updateInventory();
		},
	});

	descriptionModal = new ModalParams({});

	historyLogModal = new ModalParams();

	addProductPartModal = new ConfirmModalParams({
		onConfirm: () => {
			this.openAddExistingProductModal();
		},
		onCancel: async () => {
			this.rootStore.routerStore.goTo('master.app.main.product.list.add', {
				isUsualProductCreation: false,
				productIdForPart: this.rootStore.routerStore.routerState.params.id,
				sellerId: this.product.sellerId,
			});
		},
		yesLabel: 'PRODUCTDETAILVIEW.ADDPRODUCTPARTMODAL.ADDEXISTINGPRODUCT',
		noLabel: 'PRODUCTDETAILVIEW.ADDPRODUCTPARTMODAL.ADDNEWPRODUCT',
	});

	addExistingProductModal = new ModalParams();

	updateProductPartModal = new ModalParams();

	deleteProductPartModal = new ConfirmModalParams({
		onConfirm: () => {
			this.deleteProductPart();
		},
		onCancel: () => {
			this.deleteProductPartModal.close();
		},
	});

	productId = this.rootStore.routerStore.routerState.params.id;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ProductStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.organizationStore = new ApplicationStore(rootStore);
		this.productInventoryStore = new ProductInventoryStore(rootStore);
		this.multiPackageProductStore = new MultiPackageProductStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSeller = isSeller(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.tableData = {
			columns: [
				{
					key: 'seller.applicationTitle',
					title: 'PRODUCTDETAILVIEW.INVENTORY.WAREHOUSE',
					dataCell: DropdownCellData,
					searchBy: 'applicationTitle',
					service: this.organizationStore.service,
					warehouseOnly: true,
					includeOnlySellerWarehouses: true,
					selectedValueField: 'warehouseId',
					dataName: 'productInventory',
					updateDataItem: (idx, item) => this.updateInventoryEntry(idx, item, 'warehouseId'),
					idsToExclude: () => this.getSelectedWarehousesIds(),
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'quantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.ACTUALQUANTITY',
					selected: true,
					width: 200,
					filter: null,
					editable: this.isWarehouse ? false : true,
					editor: 'numeric',
				},
				{
					key: 'safeQuantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.SAFEQUANTITY',
					selected: true,
					width: 200,
					filter: null,
					editable: this.isWarehouse ? false : true,
					editor: 'numeric',
				},
				{
					key: 'reserved',
					title: 'PRODUCTDETAILVIEW.INVENTORY.RESERVED',
					dataCell: ReservedTooltipCellData,
					extraField: 'pendingPickup',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'warehouseOnHoldQuantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.WAREHOUSEONHOLDQUANTITY',
					selected: true,
					width: 200,
					filter: null,
					editable: this.isSuperAdminOrganization,
					editor: 'numeric',
				},
				{
					key: 'onHoldQuantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.ONHOLDQUANTITY',
					selected: true,
					width: 200,
					filter: null,
					editable: this.isWarehouse ? false : true,
					editor: 'numeric',
				},
				{
					key: 'onHoldReason',
					title: 'PRODUCTDETAILVIEW.INVENTORY.ONHOLDREASON',
					selected: true,
					width: 200,
					filter: null,
					editable: this.isWarehouse ? false : true,
					editor: 'text',
				},
			],
			sortObject: [],
			actions: {},
		};
		this.showCustomActions = !this.isWarehouse;
		this.allowEdit = !this.isWarehouse && this.rootStore.authStore.hasPermission('productSection.update');
		this.initialize();
	}

	@action initialize = async () => {
		this.suspend();
		await this.getProduct(this.productId, {
			embed: [
				'dimensionUnit',
				'weightUnit',
				'productVirtualSkus',
				'productVirtualSkus.channel',
				'productVirtualSkus.channel.channelType',
				'productVirtualSkus.channel.productInventoryRestockDate',
				'condition',
				'productCategory',
				'productStatus',
				'box',
				'productInventories',
				'productInventories.assignedWarehouse',
				'productInventories.seller',
				'multiPackageProductParts',
				'multiPackageProducts',
				'multiPackageProducts.product',
				'productUpcs',
				'productInventoryRestockDate',
			],
			calculateInventory: true,
			isSpecificWarehouse: this.isWarehouse,
			includeOnlySellerWarehouses: true,
		});
		if (this.isSeller || this.isSuperAdminOrganization) {
			await this.organizationStore.countWarehouses({
				warehouseOnly: true,
			});
			this.organizationsCount = this.organizationStore.item[0].count;
		}
		this.sellerId = this.product.sellerId;

		this.tableData.columns[0].sellerWarehousesOnlyOrganizationId = this.sellerId;
		await this.getLookups(['dimensionUnit', 'weightUnit']);
		this.convertUnits(this.product.dimensionUnit, this.product.weightUnit);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.resume();
	};

	@action.bound
	goToEdit = () => {
		this.rootStore.routerStore.goTo('master.app.main.product.list.edit', {
			id: this.rootStore.routerStore.routerState.params.id,
		});
	};

	@action.bound toggleShowMore = () => {
		this.showMore = !this.showMore;
	};

	@action.bound renderText = () => {
		if (this.product.description.length > this.textLimit && !this.showMore) {
			return `${this.product.description.substring(0, this.textLimit)}...`;
		}
		return this.product.description;
	};

	@action.bound getLookups = async keys => {
		for (let key of keys) {
			await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName[key]);
			key = key + 's';
			this[key] = this.lookupStore.data;
		}
	};

	@action.bound getProduct = async (id, options) => {
		this.product = await this.listStore.getProduct(id, options);
		const productVirtualSku = _.find(this.product.productVirtualSkus, productVirtualSku => {
			return productVirtualSku.channel.channelType.name === channelType.amazon && !productVirtualSku.isDeleted;
		});

		this.hasAmazonVirtualSku = productVirtualSku != null;

		// If restock date has passed do not show scheduled restock date. It's value will be deleted inside update inventory scheduled job.
		if (this.product != null && this.product.productInventoryRestockDate != null) {
			this.scheduledRestockDate =
				this.product.productInventoryRestockDate.restockDate != null &&
				!isCurrentDateGreater(this.product.productInventoryRestockDate.restockDate)
					? this.product.productInventoryRestockDate.scheduledRestockDateTime
					: null;
		} else {
			if (this.hasAmazonVirtualSku) {
				this.scheduledRestockDate =
					productVirtualSku.channel.productInventoryRestockDate != null &&
					productVirtualSku.channel.productInventoryRestockDate.restockDate != null &&
					!isCurrentDateGreater(productVirtualSku.channel.productInventoryRestockDate.restockDate)
						? productVirtualSku.channel.productInventoryRestockDate.scheduledRestockDateTime
						: null;
			}
		}
		if (this.scheduledRestockDate != null) {
			this.scheduledRestockDate = formatDate(this.scheduledRestockDate, true, 'LLL');
		}

		this.productPartsToShow = _.orderBy(
			_.map(this.product.multiPackageProducts, part => {
				return { mainSku: part.product.mainSku, quantity: part.quantity, extra: part };
			}),
			item => {
				return _.toLower(item.mainSku);
			}
		);
		this.prepareData(this.product);
		this.dimensionUnit = this.product.dimensionUnit.name;
		this.weightUnit = this.product.weightUnit.name;
	};

	//Prepares the data for Kendo table since data from backend is not in a good format

	@action.bound
	prepareData = product => {
		this.productInventory = [];
		_.each(product.productInventories, productInventory => {
			const inventoryItem = {
				idx: this.productInventory.length,
				id: productInventory.id,
				warehouseId: {
					label: productInventory.assignedWarehouse.applicationTitle,
					value: productInventory.assignedWarehouse.id,
					extra: productInventory.assignedWarehouse,
				},
				...productInventory,
			};
			this.productInventory.push(inventoryItem);
		});
		this.originalData = _.cloneDeep(this.productInventory.slice());
	};

	@action.bound
	convertUnits = (dimensionUnit, weightUnit) => {
		if (dimensionUnit.name === 'Inches') {
			this.convertedLenght = `${(this.product.length * this.dimensionUnits[0].conversionRate).toFixed(2)} Meters`;
			this.convertedWidth = `${(this.product.width * this.dimensionUnits[0].conversionRate).toFixed(2)} Meters`;
			this.convertedHeight = `${(this.product.height * this.dimensionUnits[0].conversionRate).toFixed(2)} Meters`;
		} else {
			this.convertedLenght = `${(this.product.length / this.dimensionUnits[0].conversionRate).toFixed(2)} Inches`;
			this.convertedWidth = `${(this.product.width / this.dimensionUnits[0].conversionRate).toFixed(2)} Inches`;
			this.convertedHeight = `${(this.product.height / this.dimensionUnits[0].conversionRate).toFixed(2)} Inches`;
		}
		if (this.product.weight > 0 && this.product.weight != null) {
			if (weightUnit.name === 'Kilograms') {
				const pounds = this.product.weight * conversionRates.kilogramsAndPounds;
				const decimalPart = pounds % 1;
				this.convertedWeight = `${pounds.toFixed(0)} lb /
				${(decimalPart * conversionRates.poundsAndOunces).toFixed(2)} oz`;
				this.product.detailWeight = `${this.product.weight} kg`;
			} else {
				const kilograms = this.product.weight / conversionRates.kilogramsAndPounds;
				const ounces = (this.product.weight % 1) * conversionRates.poundsAndOunces;
				const pounds = parseInt(this.product.weight);
				this.convertedWeight = `${kilograms.toFixed(2)} kg`;
				this.product.detailWeight = `${pounds} lb`;
				if (ounces > 0) {
					this.product.detailWeight = `${this.product.detailWeight} / ${ounces} oz`;
				}
			}
		}
	};

	@action.bound
	onInventoryLogPerWarehouse = dataItem => {
		this.selectedInventoryForInventoryLog = dataItem;
		this.inventoryLogPerWarehouseModal.open();
	};

	@action
	edit = dataItem => {
		dataItem.inEdit = true;
		if (dataItem.id) {
			const idx = _.findIndex(this.productInventory, item => {
				return item.id == dataItem.id;
			});
			this.productInventory[idx] = dataItem;
		}
		this.checkActivities();
		this.rerenderTable();
	};

	@action
	cancel = dataItem => {
		if (isSome(dataItem.id)) {
			var originalItem = this.originalData.find(item => {
				return item.id == dataItem.id;
			});
			const idx = _.findIndex(this.productInventory, item => {
				return item.id == dataItem.id;
			});
			this.productInventory[idx] = originalItem;
		} else {
			dataItem.inEdit = false;
		}
		if (this.warehouseActivities.length > 0) {
			_.remove(
				this.warehouseActivities,
				item => item.productId === dataItem.productId && item.warehouseId === dataItem.warehouseId
			);
		}
		this.checkActivities();
		this.rerenderTable();
	};

	@action
	update = dataItem => {
		if (
			isValueNullOrNegative(dataItem.quantity) ||
			isValueNullOrNegative(dataItem.safeQuantity) ||
			isValueNullOrNegative(dataItem.onHoldQuantity) ||
			isValueNullOrNegative(dataItem.warehouseOnHoldQuantity) ||
			dataItem.quantity.toString().includes('.')
		) {
			this.rootStore.notificationStore.error('PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.VALIDATIONERROR');
		} else {
			var originalItem = null;
			var existingItemIdx = null;
			if (isSome(dataItem.id)) {
				originalItem = this.originalData.find(item => {
					return item.id === dataItem.id;
				});
			}
			existingItemIdx = _.findIndex(this.warehouseActivities, item => {
				return item.inventoryIdx === dataItem.idx;
			});

			if (isSome(originalItem)) {
				if (
					dataItem.quantity != originalItem.quantity ||
					dataItem.safeQuantity != originalItem.safeQuantity ||
					dataItem.onHoldQuantity != originalItem.onHoldQuantity ||
					dataItem.warehouseOnHoldQuantity != originalItem.warehouseOnHoldQuantity ||
					dataItem.onHoldReason != originalItem.onHoldReason
				) {
					const warehouseActivity = this.createWarehouseActivity(dataItem, originalItem, false, originalItem.quantity);
					if (existingItemIdx > -1) {
						this.warehouseActivities[existingItemIdx] = warehouseActivity;
					} else {
						this.warehouseActivities.push(warehouseActivity);
					}
				} else {
					if (existingItemIdx > -1) {
						this.warehouseActivities.splice(existingItemIdx, 1);
					}
				}
			} else {
				const warehouseActivity = this.createWarehouseActivity(dataItem, originalItem, this.isInventoryAdd, 0);
				if (existingItemIdx > -1) {
					this.warehouseActivities[existingItemIdx] = warehouseActivity;
				} else {
					this.warehouseActivities.push(warehouseActivity);
				}
			}
			dataItem.inEdit = undefined;
			this.checkActivities();
			this.rerenderTable();
		}
	};

	createWarehouseActivity = (dataItem, originalItem, isInventoryAdded, quantityBefore) => {
		const quantity = dataItem.quantity - (originalItem ? originalItem.quantity : 0);
		const safeQuantity = dataItem.safeQuantity - (originalItem ? originalItem.safeQuantity : 0);
		const onHoldQuantity = dataItem.onHoldQuantity - (originalItem ? originalItem.onHoldQuantity : 0);
		const warehouseOnHoldQuantity =
			dataItem.warehouseOnHoldQuantity - (originalItem ? originalItem.warehouseOnHoldQuantity : 0);
		const onHoldReason = dataItem.onHoldReason;

		return {
			inventoryIdx: dataItem.idx,
			isReceived: dataItem.id ? false : !this.isInventoryAdd,
			isInventoryAdded: isInventoryAdded,
			warehouseId: dataItem.warehouseId.value,
			productId: dataItem.productId,
			quantity: quantity,
			safeQuantity: safeQuantity,
			onHoldQuantity: onHoldQuantity,
			warehouseOnHoldQuantity: warehouseOnHoldQuantity,
			warehouseOnHoldQuantityBefore: originalItem ? originalItem.warehouseOnHoldQuantity : 0,
			warehouseOnHoldQuantityAfter: dataItem.warehouseOnHoldQuantity,
			onHoldReason: onHoldReason,
			quantityBefore: quantityBefore,
			quantityAfter: dataItem.quantity,
		};
	};

	@action.bound
	updateInventoryEntry = (idx, data, property) => {
		this.productInventory[idx][property] = data;
		this.rerenderTable();
	};

	@action.bound
	getSelectedWarehousesIds = () => {
		this.idsToExclude = [];
		_.each(this.productInventory, item => {
			if (item.warehouseId && item.warehouseId.value) {
				this.idsToExclude.push(item.warehouseId.value);
			}
		});
		return this.idsToExclude;
	};

	@action.bound
	onItemChange = event => {
		const itemIndex = _.findIndex(this.productInventory, item => {
			return item == event.dataItem;
		});
		this.productInventory[itemIndex][event.field] = event.value;
		this.rerenderTable();
	};

	@action.bound updateInventory = async () => {
		this.suspend();
		try {
			const { description } = this.descriptionForm.values();
			_.each(this.warehouseActivities, item => {
				item.description = description;
			});
			var dataToBeSent = this.composeProductInventory();
			await this.productInventoryStore.createBatch(
				toJS({
					productInventories: dataToBeSent,
					warehouseActivities: this.warehouseActivities,
					isWarehouse: this.isWarehouse,
				}),
				sourceOfChange.updateBatchProductInventory
			);
			_.each(this.productInventory, item => {
				var existingItem = _.find(
					this.productInventoryStore.item.data,
					savedItem =>
						savedItem.productId === item.productId && savedItem.assignedWarehouseId === item.warehouseId.value
				);
				item.id = existingItem.id;
			});
			this.descriptionForm.$('description').clear();
			this.descriptionModal.close();
			this.rootStore.notificationStore.success('PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.SUCCESS');
			this.warehouseActivities = [];
			this.checkActivities();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.initialize();
	};

	composeProductInventory() {
		return _.map(this.productInventory.slice(), item => {
			return {
				id: item.id && item.id,
				productId: item.productId,
				sellerId: this.product.sellerId,
				assignedWarehouseId: item.warehouseId.value,
				quantity: item.quantity,
				pendingPickup: item.pendingPickup,
				safeQuantity: item.safeQuantity,
				reserved: item.reserved,
				onHoldQuantity: item.onHoldQuantity,
				warehouseOnHoldQuantity: item.warehouseOnHoldQuantity,
				onHoldReason: item.onHoldReason,
			};
		});
	}

	@action.bound
	onCancelBatch = () => {
		this.productInventory = [...this.originalData.slice()];
		this.warehouseActivities = [];
		this.checkActivities();
		this.rerenderTable();
	};

	@action.bound
	addRow = () => {
		this.productInventory.unshift({
			idx: this.productInventory.length,
			isNewRow: true,
			inEdit: true,
			productId: this.product.id,
			sellerId: this.product.sellerId,
			warehouseId: null,
			quantity: 0,
			safeQuantity: 3,
			reserved: 0,
			onHoldQuantity: 0,
			warehouseOnHoldQuantity: 0,
			onHoldReason: null,
		});
		this.isInventoryAdd = true;
		this.checkActivities();
		this.rerenderTable();
	};

	@action.bound
	removeRow = idx => {
		const inventoryIdx = _.findIndex(this.productInventory, item => {
			return item.idx == idx;
		});
		this.productInventory.splice(inventoryIdx, 1);
		const warehouseActivityIdx = _.findIndex(this.warehouseActivities, item => {
			return item.inventoryIdx == idx;
		});
		this.warehouseActivities.splice(warehouseActivityIdx, 1);
		this.checkActivities();
		this.rerenderTable();
	};

	@action.bound
	checkActivities = () => {
		this.confirmDisabled =
			this.warehouseActivities.length === 0 ||
			_.find(this.productInventory, item => {
				return item.inEdit === true || item.warehouseId === null || item.productId === null;
			}) != null;
		this.cancelDisabled = this.warehouseActivities.length === 0;
	};

	@action.bound
	rerenderTable() {
		this.updateTable = !this.updateTable;
	}

	@action.bound
	onProductPartDelete = index => {
		this.productPartToDeleteIndex = index;
		this.openDeleteProductPartModal();
	};

	@action.bound
	onProductPartEdit = product => {
		this.selectedProductPart = product;
		this.openUpdateProductPartModal();
	};

	@action.bound
	deleteProductPart = async () => {
		try {
			await this.multiPackageProductStore.delete({
				id: this.productPartsToShow[this.productPartToDeleteIndex].extra.id,
			});
			this.rootStore.notificationStore.success('PRODUCTDETAILVIEW.PRODUCTPARTDELETESUCCESS');
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};

	@action.bound
	openDescriptionModal = () => {
		this.descriptionModal.open();
	};

	@action.bound
	openHistoryLogModal = () => {
		this.historyLogModal.open();
	};

	@action.bound
	openAddProductPartModal = () => {
		this.addProductPartModal.open({
			message: 'PRODUCTDETAILVIEW.ADDPRODUCTPARTMODAL.MESSAGE',
		});
	};

	@action.bound
	openAddExistingProductModal = () => {
		this.addExistingProductModal.open();
	};

	@action.bound
	openDeleteProductPartModal = () => {
		this.deleteProductPartModal.open({
			message: 'PRODUCTDETAILVIEW.DELETEPRODUCTPARTMODAL.MESSAGE',
		});
	};

	@action.bound
	openUpdateProductPartModal = () => {
		this.updateProductPartModal.open();
	};

	openUpdateInventoryLogModal = () => {
		this.updateInventoryLogModal.open();
	};

	closeUpdateInventoryLogModal = () => {
		this.updateInventoryLogModal.close();
	};

	updateInventoryLogModal = new ModalParams();

	inventoryLogPerWarehouseModal = new ModalParams({
		onClose: async () => {
			this.selectedInventoryForInventoryLog = null;
		},
	});

	editRestockDateModal = new ModalParams();

	openEditRestockDateModal = () => {
		this.selectedItem = this.product;
		this.editRestockDateModal.open();
	};

	closeEditRestockDateModal = () => {
		this.selectedItem = this.product;
		this.editRestockDateModal.close();
	};
}

export default ProductDetailViewStore;



// WEBPACK FOOTER //
// ./src/modules/product/stores/ProductDetailViewStore.js